import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/Modal";
import api from "../../utils/api";
import "./Cameras.css";

const ALPHABET = "abcdefghijklmnopqrstuvwxyz";

interface Device {
  deviceId: string;
  deviceDisplayId: string;
  lot: {
    lotId: string;
  } | null;
  deviceType: string;
  isWifiRegistered: boolean;
  wifiNetworkName: string;
  wifiPassword: string;
  ipAddress: string;
  deviceStatus: string;
  lastActive: string;
  deviceTemp: number;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  isDeleted: boolean;
}

type DeviceColor = "online" | "calibrating" | "offline";

interface ParsedDevice {
  id: string;
  displayId: string;
  statusPrimary: string;
  updatedText: string;
  network: string | null;
  ipAddress: string;
  isOnline: boolean;
  colorClass: DeviceColor;
}

const formatDisplayId = (rawDisplayId: string): string => {
  const withoutPrefix = rawDisplayId.startsWith("D-")
    ? rawDisplayId.slice(2)
    : rawDisplayId;
  const lastNine = withoutPrefix.slice(-9);
  return `${lastNine.slice(0, 4)}-${lastNine.slice(4)}`;
};

const formatDeviceId = (deviceId: string): string => {
  const prefix = "PWP-D-";
  if (deviceId.startsWith(prefix)) {
    return deviceId.substring(prefix.length);
  }
  return deviceId;
};

const getRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) return "No activity";
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) return "Invalid date";

  const now = new Date();
  const diffMs = now.getTime() - parsedDate.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHr = Math.round(diffMin / 60);
  const diffDays = Math.round(diffHr / 24);

  if (diffSec < 60) return `${diffSec} seconds ago`;
  if (diffMin < 60) return `${diffMin} mins ago`;
  if (diffHr < 24) return `${diffHr} hours ago`;
  if (diffDays <= 7) return `${diffDays} days ago`;

  return parsedDate.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const parseDevice = (device: Device): ParsedDevice => {
  const {
    deviceDisplayId,
    deviceStatus,
    wifiNetworkName,
    ipAddress,
    lastActive,
  } = device;

  let colorClass: DeviceColor = "offline";
  if (deviceStatus === "Online") colorClass = "online";
  else if (deviceStatus === "Calibrating") colorClass = "calibrating";
  else if (deviceStatus === "RecentlyAdded") colorClass = "offline";

  const statusPrimary = deviceStatus === "Calibrating"
    ? "Calibrating"
    : deviceStatus === "Online"
    ? "Online"
    : "Offline";

  let updatedText = getRelativeTime(lastActive);
  if (deviceStatus === "RecentlyAdded") {
    updatedText = `Recently Added • ${updatedText}`;
  }

  return {
    id: formatDisplayId(deviceDisplayId),
    displayId: deviceDisplayId,
    statusPrimary,
    updatedText,
    network: wifiNetworkName,
    ipAddress: ipAddress || "N/A",
    isOnline: deviceStatus === "Online",
    colorClass,
  };
};

const Cameras: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [devices, setDevices] = useState<Device[]>([]);
  const [displayLotId, setDisplayLotId] = useState<string>("");
  const [nextDeviceId, setNextDeviceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState<{
    type: "add" | "remove";
    deviceId?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchLotData = useCallback(async () => {
    if (!lotId) return;
    try {
      const response = await api.get("/parkinglots/get-all-parking-lots");
      const allLots = response.data?.data;
      if (Array.isArray(allLots)) {
        const foundLot = allLots.find((lot: any) => lot.lotId === lotId);
        if (foundLot) {
          setDisplayLotId(foundLot.displayLotId || foundLot.lotId || "");
        } else {
          setDisplayLotId(lotId || "");
        }
      } else {
        setDisplayLotId(lotId || "");
      }
    } catch (err) {
      console.error("Error fetching lot data", err);
      setDisplayLotId(lotId || "");
    }
  }, [lotId]);

  const calculateNextDeviceId = useCallback(
    (lotDevices: Device[]) => {
      if (!lotId || !displayLotId) {
        setNextDeviceId(null);
        return;
      }

      const getLotNumber = (displayId: string): number => {
        const cleanLotId = displayId.startsWith("L-")
          ? displayId.substring(2)
          : displayId;
        const match = cleanLotId.match(/\d+/);
        return match ? parseInt(match[0], 10) : 1;
      };

      const deviceInfo = lotDevices.map((d) => ({
        displayId: d.deviceDisplayId,
        isDeleted: d.isDeleted,
      }));

      const devicePatterns = deviceInfo
        .map((info) => {
          const match = info.displayId.match(/D-(\d+)([a-z]+)/i);
          if (!match) return null;
          return {
            number: parseInt(match[1], 10),
            letter: match[2],
            isDeleted: info.isDeleted,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (!a || !b) return 0;
          if (a.number !== b.number) return a.number - b.number;
          return a.letter.localeCompare(b.letter);
        });

      const deletedDevice = devicePatterns.find((pattern) => pattern && pattern.isDeleted);
      if (deletedDevice) {
        const formattedNumber = String(deletedDevice.number).padStart(12, "0");
        setNextDeviceId(`D-${formattedNumber}${deletedDevice.letter}`);
        return;
      }

      const usedLetters = new Set(devicePatterns.map((pattern) => pattern!.letter));
      let nextLetter: string | null = null;
      let nextNumber: number | null = null;
      for (let i = 0; i < ALPHABET.length; i += 1) {
        if (!usedLetters.has(ALPHABET[i])) {
          nextLetter = ALPHABET[i];
          nextNumber = devicePatterns.length > 0
            ? devicePatterns[0]!.number
            : getLotNumber(displayLotId);
          break;
        }
      }

      if (!nextLetter) {
        nextNumber = (devicePatterns.length > 0
          ? devicePatterns[0]!.number
          : getLotNumber(displayLotId)) + 1;
        nextLetter = "a";
      }

      if (nextNumber !== null && nextLetter !== null) {
        const formattedNumber = String(nextNumber).padStart(12, "0");
        setNextDeviceId(`D-${formattedNumber}${nextLetter}`);
      } else {
        setNextDeviceId(null);
      }
    },
    [displayLotId, lotId]
  );

  const fetchDevices = useCallback(async () => {
    if (!lotId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/devices/get-by-lot/${lotId}`);
      const result = response.data;
      if (result?.success && Array.isArray(result.data)) {
        setDevices(result.data);
        calculateNextDeviceId(result.data);
      } else if (Array.isArray(result)) {
        setDevices(result);
        calculateNextDeviceId(result);
      } else {
        setDevices([]);
        setNextDeviceId(null);
      }
    } catch (err) {
      console.error("Error fetching devices", err);
      setError("Unable to load cameras right now.");
      setDevices([]);
      setNextDeviceId(null);
    } finally {
      setIsLoading(false);
    }
  }, [lotId, calculateNextDeviceId]);

  useEffect(() => {
    if (!lotId) return;
    fetchLotData();
    fetchDevices();
  }, [lotId, fetchLotData, fetchDevices]);

  useEffect(() => {
    calculateNextDeviceId(devices);
  }, [devices, calculateNextDeviceId]);

  const handleAddDevice = async () => {
    if (!nextDeviceId || !lotId) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await api.get(`/devices/get-all-by-lot/${lotId}`);
      const data = response.data?.data || [];
      const deletedDevice = data.find(
        (d: Device) => d.deviceDisplayId === nextDeviceId && d.isDeleted
      );

      if (deletedDevice) {
        await api.put(
          `/devices/restore-device/${lotId}/${deletedDevice.deviceId}?restoredBy=superadmin`
        );
      } else {
        const payload = {
          lotID: lotId,
          deviceType: "ALPR",
          wifiNetworkName: null,
          wifiPassword: null,
          ipAddress: null,
          createdBy: "superadmin",
        };
        await api.post("/devices/create-device", payload);
      }
      await fetchDevices();
    } catch (err) {
      console.error("Error adding device", err);
      setError("Unable to add device. Please try again.");
    } finally {
      setIsSubmitting(false);
      setModalState(null);
    }
  };

  const handleRemoveDevice = async (deviceDisplayId: string) => {
    try {
      setIsSubmitting(true);
      const device = devices.find((d) => d.deviceDisplayId === deviceDisplayId);
      if (!device) {
        setError("Device not found.");
        return;
      }

      await api.delete(`/devices/delete/${device.deviceId}/superadmin`);
      await fetchDevices();
    } catch (err) {
      console.error("Error removing device", err);
      setError("Unable to remove device. Please try again.");
    } finally {
      setIsSubmitting(false);
      setModalState(null);
    }
  };

  const parsedDevices = useMemo(() => {
    return [...devices]
      .sort((a, b) =>
        formatDeviceId(a.deviceDisplayId).localeCompare(formatDeviceId(b.deviceDisplayId))
      )
      .map((device) => parseDevice(device));
  }, [devices]);

  const openAddModal = () => setModalState({ type: "add" });
  const openRemoveModal = (deviceId: string) =>
    setModalState({ type: "remove", deviceId });

  const modalTitle = modalState?.type === "add"
    ? "Add new camera"
    : "Remove camera";
  const modalDescription = modalState?.type === "add"
    ? "A new camera will be provisioned and appear as Recently Added until configured."
    : "Removing this camera stops its reporting. You can re-add it later.";
  const modalConfirmText = modalState?.type === "add" ? "Add Camera" : "Remove Camera";

  return (
    <div className="cameras-page">
      <div className="cameras-header">
        <h1>Cameras</h1>
      </div>

      {error && <div className="cameras-error">{error}</div>}

      {isLoading ? (
        <div className="cameras-empty-state">Loading cameras…</div>
      ) : (
        <div className="table-container cameras-table-container">
          <table className="app-table cameras-table">
            <thead>
              <tr>
                <th>Camera</th>
                <th>Network</th>
                <th>Controls</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {parsedDevices.length === 0 && (
                <tr>
                  <td colSpan={5} className="cameras-empty-state">No cameras found for this lot.</td>
                </tr>
              )}
              {parsedDevices.map((device) => (
                <tr key={device.displayId} className={`cameras-row ${device.colorClass}`}>
                  <td>
                    <div className="cameras-id-wrapper">
                      <div className="cameras-id-text">
                        <span className="cameras-id-label">{device.id}</span>
                        <span className="cameras-status-primary">{device.statusPrimary}</span>
                        <span className="cameras-status-secondary">{device.updatedText}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="cameras-network">
                      <span className="cameras-network-label">{device.network || "No network"}</span>
                      <a
                        href={`http://${device.ipAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="cameras-network-ip"
                      >
                        {device.ipAddress}
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="cameras-controls">
                      <button
                        className="cameras-inline-button"
                        type="button"
                        disabled={!device.isOnline}
                      >
                        <img src="/assets/superadmin/cameras/shutdown.svg" alt="Shutdown" />
                        Shutdown
                      </button>
                      <button
                        className="cameras-inline-button"
                        type="button"
                        disabled={!device.isOnline}
                      >
                        <img src="/assets/superadmin/cameras/restart.svg" alt="Reboot" />
                        Reboot
                      </button>
                    </div>
                  </td>
                  <td className="cameras-remove-cell">
                    <button
                      type="button"
                      className="cameras-remove-button"
                      onClick={() => openRemoveModal(device.displayId)}
                      aria-label={`Remove ${device.displayId}`}
                    >
                      <img src="/assets/Minus.svg" alt="Remove camera" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="cameras-row cameras-add-row" onClick={openAddModal}>
                <td>
                  <div className="cameras-id-wrapper">
                    <div className="cameras-id-text">
                      <span className="cameras-id-label">
                        {nextDeviceId ? formatDisplayId(nextDeviceId) : "New camera"}
                      </span>
                      <span className="cameras-status-primary">+ Add New Camera</span>
                    </div>
                  </div>
                </td>
                <td></td>
                <td></td>
                <td className="cameras-remove-cell">
                  <button
                    type="button"
                    className="cameras-add-action"
                    onClick={openAddModal}
                    disabled={!nextDeviceId}
                  >
                    <img src="/assets/Plus.svg" alt="Add camera" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={Boolean(modalState)}
        title={modalTitle}
        description={modalDescription}
        confirmText={modalConfirmText}
        cancelText="Cancel"
        onCancel={() => setModalState(null)}
        onConfirm={() =>
          modalState?.type === "add"
            ? handleAddDevice()
            : modalState?.deviceId
            ? handleRemoveDevice(modalState.deviceId)
            : undefined
        }
        disableConfirm={isSubmitting || (modalState?.type === "add" && !nextDeviceId)}
      />
    </div>
  );
};

export default Cameras;
